import React, {useEffect, useRef, useState} from 'react';
import {
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import {useSafeAreaInsets} from 'react-native-safe-area-context';
import Svg, {Path} from 'react-native-svg';
import {Trans, useTranslation} from 'react-i18next';
import {useDispatch} from 'react-redux';
import Toast from 'react-native-toast-message';
import ArrowRight from '../../assets/icons/arrow-right.svg';
import MailIcon from '../../assets/icons/mail.svg';
import AuthHeader from '../../components/AuthHeader';
import AuthButton from '../../components/AuthButton';
import {colors, fonts, radii} from '../../config/theme';
import {
  requestPasswordReset,
  sendSignupOtp,
  signup,
  verifyResetOtp,
  verifySignupOtp,
} from '../../services/auth-service';
import {loginUser} from '../../slice/loginSlice';

const CODE_LENGTH = 6;
// The server refuses a resend within 60 s (429 RESEND_TOO_SOON).
const RESEND_COOLDOWN_SECONDS = 60;

const RefreshGlyph = () => (
  <Svg
    width={14}
    height={14}
    viewBox="0 0 24 24"
    fill="none"
    stroke={colors.primary}
    strokeWidth={2.4}
    strokeLinecap="round"
    strokeLinejoin="round">
    <Path d="M21 12a9 9 0 1 1-2.6-6.4M21 4v5h-5" />
  </Svg>
);

// The 6-digit email code, for both flows:
//   mode 'signup'  params {email, signup: <auth/signup body>}
//                  CreateAccount already sent the code. Once it checks out
//                  the account is created and the user logged in.
//   mode 'reset'   params {email}
//                  ForgotPassword already sent the code. Once it checks out
//                  UpdatePassword sets the new password with the resetToken.
const VerifyOtp = ({navigation, route}) => {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const insets = useSafeAreaInsets();
  const {mode = 'signup', email, signup: signupBody} = route.params ?? {};
  const isReset = mode === 'reset';

  const [code, setCode] = useState('');
  const [focused, setFocused] = useState(false);
  const [error, setError] = useState();
  const [verifying, setVerifying] = useState(false);
  const [resending, setResending] = useState(false);
  const [cooldown, setCooldown] = useState(RESEND_COOLDOWN_SECONDS);
  const inputRef = useRef(null);
  // The code last sent to the server, so auto-submit fires once per code.
  const submittedCode = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) {
      return undefined;
    }
    const timer = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  const verify = async value => {
    if (verifying) {
      return;
    }
    if (value.length !== CODE_LENGTH) {
      setError(t('root.VerifyOtp.codeIncomplete'));
      return;
    }
    submittedCode.current = value;
    setVerifying(true);
    try {
      if (isReset) {
        const data = await verifyResetOtp(email, value);
        navigation.replace('UpdatePassword', {
          email,
          resetToken: data?.resetToken,
        });
        return;
      }

      const verified = await verifySignupOtp(email, value);
      try {
        // signup doesn't check verificationToken yet, but sending it now
        // means nothing changes here once it does.
        await signup({
          ...signupBody,
          ...(verified?.verificationToken
            ? {verificationToken: verified.verificationToken}
            : {}),
        });
      } catch (err) {
        // The code was right but the account could not be made (e.g. the
        // email is taken): back to the form, which still has the details.
        Toast.show({
          type: 'error',
          text1: err.message || t('root.Toast.Error.SignupFailed'),
        });
        navigation.goBack();
        return;
      }
      Toast.show({type: 'success', text1: t('root.Toast.Success.SignupSuccess')});
      // Logging in switches the app to onboarding by itself.
      dispatch(loginUser({email: signupBody.email, password: signupBody.password}));
    } catch (err) {
      console.log('verify otp error', err);
      setError(err?.message || t('root.VerifyOtp.verifyFailed'));
    } finally {
      setVerifying(false);
    }
  };

  const onChangeCode = value => {
    const digits = value.replace(/\D/g, '').slice(0, CODE_LENGTH);
    setCode(digits);
    if (error) {
      setError(undefined);
    }
    // Typed or pasted the whole code: check it straight away.
    if (digits.length === CODE_LENGTH && submittedCode.current !== digits) {
      verify(digits);
    }
  };

  const handleResend = async () => {
    setResending(true);
    try {
      if (isReset) {
        await requestPasswordReset(email);
      } else {
        await sendSignupOtp(email, signupBody?.phone_number);
      }
      setCode('');
      setError(undefined);
      submittedCode.current = null;
      setCooldown(RESEND_COOLDOWN_SECONDS);
      Toast.show({type: 'success', text1: t('root.VerifyOtp.resent')});
    } catch (err) {
      Toast.show({
        type: 'error',
        text1: t('root.VerifyOtp.resendFailed'),
        text2: err?.message,
      });
    } finally {
      setResending(false);
    }
  };

  const mm = String(Math.floor(cooldown / 60)).padStart(2, '0');
  const ss = String(cooldown % 60).padStart(2, '0');

  return (
    <View style={styles.screen}>
      <AuthHeader
        title={t('root.VerifyOtp.title')}
        onBack={() => navigation.goBack()}
      />
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}>
        <ScrollView
          style={styles.flex}
          contentContainerStyle={[
            styles.scrollContent,
            {paddingBottom: insets.bottom + 24},
          ]}
          keyboardShouldPersistTaps="handled"
          showsVerticalScrollIndicator={false}>
          <View style={styles.mailBadge}>
            <MailIcon width={30} height={30} color={colors.brandBright} />
          </View>
          <Text style={styles.heading} accessibilityRole="header">
            {t('root.VerifyOtp.heading')}
          </Text>
          <Text style={styles.copy}>
            <Trans
              i18nKey={isReset ? 'root.VerifyOtp.bodyReset' : 'root.VerifyOtp.body'}
              values={{email}}
              components={{email: <Text style={styles.copyStrong} />}}
            />
          </Text>

          <View style={styles.card}>
            <Text style={styles.cardLabel}>{t('root.VerifyOtp.securityCode')}</Text>

            {/* One real input under six drawn boxes: paste, the keyboard's
                "from Mail" suggestion and backspace all just work. */}
            <Pressable
              onPress={() => inputRef.current?.focus()}
              style={styles.boxes}
              accessible={false}>
              {Array.from({length: CODE_LENGTH}, (_, i) => {
                const digit = code[i];
                const active =
                  focused &&
                  (i === code.length ||
                    (i === CODE_LENGTH - 1 && code.length === CODE_LENGTH));
                return (
                  <View
                    key={i}
                    style={[
                      styles.box,
                      digit && styles.boxFilled,
                      active && styles.boxActive,
                      error && styles.boxError,
                    ]}>
                    <Text style={[styles.digit, !digit && styles.digitEmpty]}>
                      {digit || '•'}
                    </Text>
                  </View>
                );
              })}
              <TextInput
                ref={inputRef}
                value={code}
                onChangeText={onChangeCode}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                keyboardType="number-pad"
                inputMode="numeric"
                textContentType="oneTimeCode"
                autoComplete={Platform.OS === 'android' ? 'sms-otp' : 'one-time-code'}
                maxLength={CODE_LENGTH}
                autoFocus
                caretHidden
                selectionColor="transparent"
                accessibilityLabel={t('root.VerifyOtp.securityCode')}
                style={styles.hiddenInput}
              />
            </Pressable>

            {error ? (
              <Text style={styles.error} accessibilityLiveRegion="polite">
                {error}
              </Text>
            ) : null}

            <AuthButton
              variant="primary"
              title={t('root.VerifyOtp.submit')}
              iconRight={<ArrowRight width={16} height={16} color={colors.onPrimary} />}
              loading={verifying}
              onPress={() => verify(code)}
              style={styles.submit}
            />

            <View style={styles.resend}>
              <Text style={styles.muted}>{t('root.VerifyOtp.notReceived')}</Text>
              {cooldown > 0 ? (
                <View style={styles.timerPill}>
                  <Text style={styles.timerText}>
                    {t('root.VerifyOtp.resendIn')}{' '}
                    <Text style={styles.timerStrong}>
                      {mm}:{ss}
                    </Text>
                  </Text>
                </View>
              ) : (
                <Pressable
                  onPress={handleResend}
                  disabled={resending}
                  hitSlop={8}
                  accessibilityRole="button"
                  style={({pressed}) => [
                    styles.resendPill,
                    (pressed || resending) && styles.pressed,
                  ]}>
                  <RefreshGlyph />
                  <Text style={styles.resendText}>{t('root.VerifyOtp.resend')}</Text>
                </Pressable>
              )}
            </View>
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </View>
  );
};

const styles = StyleSheet.create({
  screen: {
    flex: 1,
    backgroundColor: colors.background,
  },
  flex: {
    flex: 1,
  },
  scrollContent: {
    flexGrow: 1,
    paddingHorizontal: 20,
    paddingTop: 36,
  },
  mailBadge: {
    alignSelf: 'center',
    width: 72,
    height: 72,
    borderRadius: radii.pill,
    backgroundColor: colors.mintWash,
    borderWidth: 1,
    borderColor: colors.mintLine,
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: 16,
  },
  heading: {
    fontFamily: fonts.bold,
    fontSize: 24,
    lineHeight: 30,
    letterSpacing: -0.3,
    color: colors.primary,
    textAlign: 'center',
  },
  copy: {
    fontFamily: fonts.regular,
    fontSize: 13.5,
    lineHeight: 21,
    color: colors.textMuted,
    textAlign: 'center',
    marginTop: 8,
    paddingHorizontal: 16,
  },
  copyStrong: {
    fontFamily: fonts.semiBold,
    color: colors.textEmphasis,
  },
  card: {
    marginTop: 28,
    backgroundColor: colors.surface,
    borderRadius: radii.card,
    borderWidth: 1,
    borderColor: colors.borderSubtle,
    padding: 20,
    ...Platform.select({
      ios: {
        shadowColor: colors.primary,
        shadowOffset: {width: 0, height: 8},
        shadowOpacity: 0.07,
        shadowRadius: 18,
      },
      // RN 0.77 draws square elevation under big radii (see theme.js), so
      // Android leans on a firmer border instead.
      default: {borderColor: colors.border},
    }),
  },
  cardLabel: {
    fontFamily: fonts.semiBold,
    fontSize: 11,
    lineHeight: 14,
    letterSpacing: 0.8,
    textTransform: 'uppercase',
    color: colors.textSubtle,
    marginBottom: 12,
    paddingHorizontal: 2,
  },
  boxes: {
    flexDirection: 'row',
    gap: 8,
  },
  box: {
    flex: 1,
    height: 56,
    borderRadius: radii.sm,
    backgroundColor: colors.surfaceMuted,
    borderWidth: 2,
    borderColor: 'transparent',
    alignItems: 'center',
    justifyContent: 'center',
  },
  boxFilled: {
    backgroundColor: colors.mintWash,
  },
  boxActive: {
    backgroundColor: colors.surface,
    borderColor: colors.secondary,
  },
  boxError: {
    borderColor: colors.error,
    backgroundColor: colors.errorTint,
  },
  digit: {
    fontFamily: fonts.semiBold,
    fontSize: 22,
    lineHeight: 28,
    color: colors.primary,
  },
  digitEmpty: {
    color: colors.textSubtle,
    opacity: 0.5,
  },
  // Covers the boxes so a tap anywhere lands in it, but stays invisible.
  hiddenInput: {
    ...StyleSheet.absoluteFillObject,
    opacity: 0.011,
    color: 'transparent',
    fontSize: 1,
  },
  error: {
    fontFamily: fonts.medium,
    fontSize: 12,
    lineHeight: 16,
    color: colors.error,
    marginTop: 10,
    textAlign: 'center',
  },
  submit: {
    marginTop: 20,
  },
  resend: {
    alignItems: 'center',
    marginTop: 18,
    gap: 6,
  },
  muted: {
    fontFamily: fonts.regular,
    fontSize: 12,
    lineHeight: 16,
    color: colors.textMuted,
  },
  timerPill: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: radii.pill,
    backgroundColor: colors.surfaceMuted,
  },
  timerText: {
    fontFamily: fonts.medium,
    fontSize: 12,
    lineHeight: 16,
    color: colors.textMuted,
  },
  timerStrong: {
    fontFamily: fonts.semiBold,
    color: colors.textStrong,
  },
  resendPill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 5,
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: radii.pill,
    backgroundColor: colors.primaryFixed,
  },
  resendText: {
    fontFamily: fonts.semiBold,
    fontSize: 12,
    lineHeight: 16,
    color: colors.primary,
  },
  pressed: {
    opacity: 0.6,
  },
});

export default VerifyOtp;
